import type { Ref } from 'react';

export type LevelRingProps = {
  level: number;
  xp: number;
  xpToNext: number;
  /** Unspent stat points. When > 0 AND `onAllocate` is given, the ring becomes the tap target
   *  for the allocate sheet and carries a "+N" badge. Omitted/0 leaves the ring a plain
   *  `role="img"` exactly as before (the gallery and any other caller are unaffected). */
  freePoints?: number;
  /** Opens the allocate sheet. See `freePoints`. */
  onAllocate?: () => void;
  /** The allocate sheet's `restoreFocusRef` target — BottomSheet.tsx's doc comment on why focus
   *  return needs the real trigger element. Attaches to the ring's own button. */
  allocateRef?: Ref<HTMLButtonElement>;
};

// Arbitrary internal coordinate space (viewBox only — not tied to the --sp-* px scale, unlike a
// literal pixel size would be) so the ring's actual rendered size is driven entirely by the
// `.level-ring` CSS class (base.css) rather than a fixed px attribute here.
const VIEWBOX = 40;
const STROKE = 5; // raw-ok: SVG stroke-width in viewBox units, not a CSS px literal
const RADIUS = (VIEWBOX - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * SCREENS.md /habitat "level-ring": the XP ring + level number on the companion card. A single
 * accessible name (`role="img"`, exact "Level N — X of Y XP" format the task-21 brief pins) carries
 * the whole ring's meaning for assistive tech — the SVG itself and the visible level number are
 * both `aria-hidden`, so nothing is announced twice. §11.8 "never colour alone": the progress
 * fraction is ALSO always the literal "X of Y XP" text in that one label, never conveyed by the
 * arc's fill alone.
 *
 * Colors come from `.level-ring-track`/`.level-ring-progress` (base.css, tokens-only) via
 * `stroke="currentColor"` — the same `currentColor`-driven-by-a-CSS-class idiom shell/glyphs.tsx's
 * own header comment documents, rather than a raw hex/var() literal on the SVG attribute itself.
 */
export function LevelRing({
  level, xp, xpToNext, freePoints = 0, onAllocate, allocateRef,
}: LevelRingProps) {
  // Guards a divide-by-zero (xpToNext should always be > 0 per XpService, but a defensive floor
  // here costs nothing and keeps the ring's math well-defined regardless).
  const ratio = xpToNext > 0 ? Math.min(1, Math.max(0, xp / xpToNext)) : 0;
  const dashoffset = CIRCUMFERENCE * (1 - ratio);
  const allocatable = freePoints > 0 && onAllocate != null;
  const ringLabel = `Level ${level} — ${xp} of ${xpToNext} XP`;

  return (
    // The ring keeps a single accessible name whether or not it is tappable: when there are
    // points to spend the button's own name appends them, so nothing is announced twice and the
    // XP fraction is never lost. `.level-ring` is --sp-7, which clears §11.3's touch-target
    // floor on its own — the "+N" badge is decoration and must never be the hit area itself.
    <div className="level-ring" role={allocatable ? undefined : 'img'} aria-label={allocatable ? undefined : ringLabel}>
      <svg viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`} aria-hidden="true">
        <circle
          className="level-ring-track"
          cx={VIEWBOX / 2} cy={VIEWBOX / 2} r={RADIUS}
          fill="none" stroke="currentColor" strokeWidth={STROKE}
        />
        <circle
          className="level-ring-progress"
          cx={VIEWBOX / 2} cy={VIEWBOX / 2} r={RADIUS}
          fill="none" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={dashoffset}
          transform={`rotate(-90 ${VIEWBOX / 2} ${VIEWBOX / 2})`}
        />
      </svg>
      <span className="level-ring-number" aria-hidden="true">{level}</span>
      {allocatable && (
        <button
          ref={allocateRef}
          type="button"
          className="level-ring-allocate"
          aria-label={`${ringLabel}. ${freePoints} point${freePoints === 1 ? '' : 's'} to spend`}
          onClick={onAllocate}
        >
          <span className="allocate-points-badge" aria-hidden="true">+{freePoints}</span>
        </button>
      )}
    </div>
  );
}
